import React, { useMemo } from 'react'
import { Stack } from '@mui/material'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ScrollTable from 'components/global/CUK/ScrollTable'
import TableView, { useTable, IItem } from 'packages/ui/Table'
import EmptyScreen from 'packages/ui/EmptyScreen'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import { ResponsiveTableProvider, useResponsiveColumns } from 'packages/ui/Table'
import { useSidebar } from 'components/AuthenticatedLayout/Sidebar/SidebarContext'

import CONFIG_COLUMNS, { ExpandedRowDetails } from './config-columns'
import { useSort } from '../store'

interface TableProps {
  apiUsers: {
    loading: boolean;
    loadingNext: boolean;
    fetchNextPage: () => void;
    shouldFetchNextPage: boolean;
    users: IItem[];
    hasCivility: boolean;
  };
}

const RESPONSIVE_COLUMNS_CONFIG = {
  mobile: ['civility', 'first_name', 'last_name', 'email', 'actions'],
  tablet: ['civility', 'first_name', 'last_name', 'email', 'site', 'country', 'actions'],
  desktop: ['civility', 'first_name', 'last_name', 'email', 'account', 'site', 'country', 'created_at'],
  desktopLarge: ['civility', 'first_name', 'last_name', 'email', 'account', 'site', 'country', 'created_at']
}

const Table: React.FC<TableProps> = ({ apiUsers }) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [sort, setSort] = useSort()
  const navigate = useNavigate()
  const { expanded: sidebarExpanded } = useSidebar()
  
  const excludeColumns = useMemo(() => {
    const excluded = id ? ['account', 'site'] : []
    if (!apiUsers.hasCivility) {
      excluded.push('civility')
    }
    return excluded
  }, [id, apiUsers.hasCivility])
  
  const { visibleColumns, getVisibleColumns } = useResponsiveColumns(
    RESPONSIVE_COLUMNS_CONFIG,
    {
      sidebarExpanded,
      excludeColumns
    }
  )

  const columns = useMemo(() => {
    return CONFIG_COLUMNS.filter(({ name }) => visibleColumns.includes(name))
  }, [visibleColumns])

  const responsiveConfig = useMemo(() => ({
    expandedRowRenderer: {
      component: ExpandedRowDetails,
      shouldRender: (screenSize) => screenSize === 'mobile',
      getVisibleColumns,
      additionalProps: { hasCivility: apiUsers.hasCivility }
    }
  }), [getVisibleColumns, apiUsers.hasCivility])

  const apiTable = useTable({
    rowKey: 'id',
    loading: apiUsers.loading,
    loadingNext: apiUsers.loadingNext,
    items: apiUsers.users,
    columns,
    onRowClick: user => {
      navigate(`/stats/customers/${user.id}`)
    },
    responsiveConfig
  })

  const isEmpty = !apiUsers.loading && (!apiUsers.users || apiUsers.users.length === 0)

  return (
    <ResponsiveTableProvider>
      <Stack
        sx={{
          overflow: 'hidden',
          overflowY: 'auto',
          flex: '1 1 auto',
          scrollbarWidth: 'none',
          height: '100%',
          '&::-webkit-scrollbar ': {
            display: 'none'
          }
        }}
      >
        <ScrollTable
          fetchNextPage={apiUsers.fetchNextPage}
          shouldFetchNextPage={apiUsers.shouldFetchNextPage}
          collapse={!!id}
          isEmpty={isEmpty}
        >
          {apiUsers.loading ? (
            <TableSkeleton />
          ) : isEmpty ? (
            <EmptyScreen icon="square-poll-vertical">{t('statistics.no_data_filter')}</EmptyScreen>
          ) : (
            <TableView
              apiTable={apiTable}
              sort={{
                by: sort.by,
                direction: sort.direction,
                onChange: (name: string) => {
                  const column = columns.find(column => column.name === name)
                  if (column) {
                    setSort(column.sortBy || column.name)
                  }
                }
              }}
            />
          )}
        </ScrollTable>
      </Stack>
    </ResponsiveTableProvider>
  )
}

export default Table
